import React, { useState, useEffect } from 'react'
import { Card, Form, Input, Button, Alert, Typography, Space } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import QRCodeWrapper from 'components/QrCode/QrCode'
import { useAuthMfaSecure } from 'providers/AuthSecureManage/AuthMfaSecureManage'
import styles from './MfaSetup.module.css'

const { Text } = Typography

const MfaSetup = ({ user, onComplete, onCancel }) => {
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [factorId, setFactorId] = useState('')
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [step, setStep] = useState(1) ;
  
  const { enrollMFA, verifyChallengeMFA, createChallengeMFA } = useAuthMfaSecure();

  useEffect(() => {
    window.history.pushState(null, '', window.location.href)
    
    const handlePopState = (event) => {
      window.history.pushState(null, '', window.location.href)
      setError('No puede regresar durante la configuración MFA. Use el botón "Cancelar" para volver al login.')
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  const setupMFA = async () => {
    try {
      setLoading(true)
      setError('')
      
      // Generar nombre aleatorio único
      const deviceTypes = ['iPhone', 'Android', 'Tablet', 'MacBook', 'PC', 'Laptop']
      const adjectives = ['Personal', 'Trabajo', 'Principal', 'Secundario', 'Casa', 'Oficina']
      const randomDevice = deviceTypes[Math.floor(Math.random() * deviceTypes.length)]
      const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)]
      const randomNumber = Math.floor(Math.random() * 999) + 1
      
      const deviceName = `${randomDevice} ${randomAdjective} ${randomNumber}`
      
      const { id, totp } = await enrollMFA(deviceName);
      
      setFactorId(id)
      setQrCode(totp.qr_code)
      setSecret(totp.secret)
      setStep(2)
    } catch (error) {
      console.error('Error en MFA setup:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async (values) => {
    const { code: formCode } = values

    try {
      setLoading(true)
      setError('')

      // Para setup, necesitamos crear un challenge específico para verificación
      console.log('🎯 Creando challenge para verificación de setup...')
      const challenge = await createChallengeMFA(factorId)

      // Verificar con el challenge recién creado
      await verifyChallengeMFA(factorId, formCode || code, challenge.id)
      console.log('🎉 MFA setup verificado exitosamente')
      onComplete()
    } catch (error) {
      console.error('❌ Error verificando MFA setup:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const copySecret = () => {
    navigator.clipboard.writeText(secret)
  }


  if (step === 1) {
    return (
      <div className={styles.containerPreview}>
        <div className={styles.formContainer}>
          <div className={styles.header}>
            <div className={styles.iconContainer}>🔒</div>
            <div style = {{width: '70%', margin: 'auto'}}>
            <h2 className={styles.title}>
              Configuración MFA Obligatoria
            </h2>
            <p className={styles.subtitle}>
              Debe configurar autenticación de dos factores para continuar
            </p>
            </div>

          </div>

          {error && (
            <Alert
              message={error}
              type="error"
              icon={<span>❌</span>}
              showIcon
              style={{ marginBottom: '1.5rem' }}
            />
          )}

          <div style = {{width: '70%', margin: 'auto'}}>
          <Alert
            message="Acceso Bloqueado"
            description="No puede acceder al sistema sin configurar MFA. Esta es una medida de seguridad obligatoria."
            type="error"
            icon={<span>🚨</span>}
            showIcon
            style={{ marginBottom: '1.5rem' }}
          />

          <Card style={{ borderRadius: 12 }}>
            <div className={styles.buttonGroup}>
              <Button
                type="primary"
                size="large"
                block
                onClick={setupMFA}
                loading={loading}
              >
                {loading ? 'Generando MFA...' : 'Configurar MFA Ahora'}
              </Button>

              <Button
                size="large"
                block
                onClick={onCancel}
                disabled={loading}
              >
                Cancelar Login
              </Button>
            </div>
          </Card>
          </div>

        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.formContainer}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            Escanea el Código QR
          </h2>
          <p className={styles.subtitle}>
            Configure su aplicación autenticadora y verifique el código
          </p>
        </div>

        <div className={ styles.contentContainer }>

       
        {qrCode && (
          <Card style={{ borderRadius: 12, marginBottom: '1.5rem' , width: '70%'}}>
            <div className={styles.qrContainer}>
              <h3 className={styles.qrTitle}>
                Paso 1: Escanee con su aplicación
              </h3>

              <div className={styles.qrCode}>
                <QRCodeWrapper value={qrCode} size={200} />
              </div>

              <div className={styles.secretContainer}>
                <p className={styles.secretLabel}>
                  O ingrese manualmente esta clave:
                </p>
                <div className={styles.secretDisplay}>
                  <Text code className={styles.secretCode}>
                    {secret}
                  </Text>
                  <Button
                    type="link"
                    icon={<CopyOutlined />}
                    onClick={copySecret}
                    size="small"
                  >
                    Copiar
                  </Button>
                </div>
              </div>

              <div className={styles.appsList}>
                <p>Aplicaciones compatibles:</p>
                <p>Google Authenticator • Authy • 1Password • Microsoft Authenticator</p>
              </div>
            </div>
          </Card>
        )}

        <Card style={{ borderRadius: 12 , marginLeft: '2rem'}}>
          <Form onFinish={handleVerify} layout="vertical">
            {error && (
              <Alert
                message={error}
                type="error"
                icon={<span>❌</span>}
                showIcon
                style={{ marginBottom: '1rem' }}
              />
            )}

            <div className={styles.verificationSection}>
              <h3 className={styles.verificationTitle}>
                Paso 2: Verificar configuración
              </h3>

              <Alert
                message="Verificación de Configuración"
                description="Ingrese el código de 6 dígitos que aparece en su aplicación autenticadora para confirmar que la configuración fue exitosa."
                type="info"
                icon={<span>ℹ️</span>}
                showIcon
                style={{ marginBottom: '1rem' }}
              />

              <Form.Item
                name="code"
                label="Código de verificación de 6 dígitos"
                rules={[
                  { required: true, message: 'Ingrese el código' },
                  {
                    pattern: /^\d{6}$/,
                    message: 'Debe ser un código de exactamente 6 dígitos'
                  }
                ]}
              >
                <Input
                  className={styles.codeInput}
                  placeholder="000000"
                  maxLength={6}
                  type="text"
                  size="large"
                  value={code}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(value);
                  }}
                  autoFocus
                />
               
              </Form.Item>
              <div className={styles.codeHelp}>
                  Este código cambia cada 30 segundos en su aplicación
                </div>
            </div>

            <div className={styles.buttonGroup}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={loading}
              >
                {loading ? 'Verificando...' : 'Completar Configuración MFA'}
              </Button>


            </div>
          </Form>
        </Card>
        </div>

        <div className={styles.cancelButton}>
            <Button className={styles.header}
                size="large"
                block
                onClick={onCancel}
              >
                Volver al Login
              </Button>
        </div>
        
      </div>
    </div>
  )
}

export default MfaSetup